// Picking-job rate versions (handoff §7g, Picking jobs view). What a version
// is in force for, and whether a change lands in a reported period, mirror
// routes/picking-jobs.js; the server decides and names the figures, these only
// say in advance which kind of change the panel is looking at.
import { dmy } from '../wages/format'
import { TEAM_COLOURS } from '../TeamsPage'

export interface RateVersion {
  id: string
  effective_from: string
  effective_to: string | null
  rate_per_kg: number
  status: 'current' | 'past' | 'scheduled'
}

// The server's figures for a change that restates reported picking
// (409 RESTATEMENT_ACK_REQUIRED).
export interface Restatement {
  from: string
  to: string | null
  old_rate: number | null
  new_rate: number | null
  kg_recorded: number
}

export const perKg = (v: number) => `$${Number(v).toFixed(2)}/kg`

// "D – D", or "D onwards" for the version with no successor.
export const period = (from: string, to: string | null) => (to ? `${dmy(from)} – ${dmy(to)}` : `${dmy(from)} onwards`)

export const STATE_LABEL = { current: 'Current', past: 'Past', scheduled: 'Scheduled' } as const

export type RateMode = 'add' | 'edit' | 'remove'

export interface RateDraft {
  mode: RateMode
  version: RateVersion | null
  rate: string
  from: string
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
// NUMERIC(10,2) in 016: a rate is to the cent.
const RATE_RE = /^\d+(\.\d{1,2})?$/

export const parseRate = (s: string) => (RATE_RE.test(s.trim()) ? Number(s.trim()) : NaN)

// "has this version ever been in force?" A backdated add, an edit whose old or
// new date is on or before today, and removing a version that has started
// all restate reported picking (§7g).
export function hitsPast(d: RateDraft, today: string): boolean {
  if (d.mode === 'remove') return !!d.version && d.version.effective_from <= today
  if (!DATE_RE.test(d.from)) return false
  if (d.mode === 'add') return d.from <= today
  return !!d.version && (d.from <= today || d.version.effective_from <= today)
}

// "Removing a version hands its period to the version before it."
export function takesOver(versions: RateVersion[], v: RateVersion): RateVersion | null {
  return (
    versions
      .filter((x) => x.effective_from < v.effective_from)
      .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1))
      .pop() || null
  )
}

export function current(versions: RateVersion[]): RateVersion | null {
  return versions.find((v) => v.status === 'current') || null
}

export function firstScheduled(versions: RateVersion[]): RateVersion | null {
  return (
    versions
      .filter((v) => v.status === 'scheduled')
      .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1))[0] || null
  )
}

// What stops the dialog saving, before the server is asked.
export function draftErrors(d: RateDraft, versions: RateVersion[]): string[] {
  if (d.mode === 'remove') return []
  const errs: string[] = []
  const rate = parseRate(d.rate)
  if (!(rate > 0)) errs.push('Enter a rate above $0.00 per kg, to the cent.')
  if (!DATE_RE.test(d.from)) errs.push('Choose the date this rate takes effect.')
  else {
    const same = versions.find((v) => v.effective_from === d.from && v.id !== d.version?.id)
    // "Duplicate effective dates are a conflict, not a merge."
    if (same) errs.push(`A version already starts on ${dmy(d.from)}. Edit that version instead.`)
  }
  if (d.mode === 'edit' && d.version && !errs.length && rate === d.version.rate_per_kg && d.from === d.version.effective_from) {
    errs.push('Nothing has changed.')
  }
  return errs
}

// The explanation above the acknowledgement (V4 ratePastBody). The kg come
// from the server's restatement, not from here.
export function pastBody(d: RateDraft, versions: RateVersion[]): string {
  const rate = parseRate(d.rate)
  const next = rate > 0 ? perKg(rate) : 'the new rate'
  const v = d.version
  if (d.mode === 'remove' && v) {
    const prev = takesOver(versions, v)
    return (
      `Picking already recorded from ${dmy(v.effective_from)} was priced at ${perKg(v.rate_per_kg)}. Removing this version means ` +
      (prev ? `${perKg(prev.rate_per_kg)} from ${dmy(prev.effective_from)} covers that period instead` : 'no rate covers that period') +
      ', so anything already reported or paid for it is restated.'
    )
  }
  if (d.mode === 'add' || !v) {
    return `This date is on or before today, so it lands in a period that has already been reported. Picking recorded in it is repriced at ${next}.`
  }
  if (v.status === 'scheduled') {
    return `Moving this version to start ${dmy(d.from)} puts it in a period that has already been reported. Picking recorded from then is repriced at ${next}.`
  }
  return (
    `Picking already recorded under this version was priced at ${perKg(v.rate_per_kg)}. Saving reprices it at ${next}` +
    (d.from !== v.effective_from ? ` and moves the period it covers to start ${dmy(d.from)}` : '') +
    '. Yield, cost and payroll reports covering those dates change.'
  )
}

export function pastTitle(d: RateDraft): string {
  return d.mode === 'remove'
    ? 'Removing a rate that has been in force'
    : d.mode === 'add'
      ? 'Backdating a rate into a reported period'
      : d.version?.status === 'scheduled'
        ? 'Moving a scheduled rate into a reported period'
        : 'Amending a rate that has been in force'
}

// "defaulted to the colour least used in that Site so a new job never lands on
// one already doing work beside it". Archived jobs do no work, so they do not
// count; ties go to the palette's order.
export function leastUsedColour(colours: (string | null | undefined)[]): string {
  const uses = new Map(TEAM_COLOURS.map((c) => [c.hex, 0]))
  for (const c of colours) {
    const k = (c || '').toLowerCase()
    if (uses.has(k)) uses.set(k, uses.get(k)! + 1)
  }
  let best = TEAM_COLOURS[0].hex
  for (const c of TEAM_COLOURS) if (uses.get(c.hex)! < uses.get(best)!) best = c.hex
  return best
}

export const colourName = (hex: string | null | undefined) => TEAM_COLOURS.find((c) => c.hex === (hex || '').toLowerCase())?.name || 'Pick one'
